import type { ComponentType } from 'react';
import {
  ArrowSquareOutIcon,
  CertificateIcon,
  GlobeStandIcon,
  KeyIcon,
  LockKeyIcon,
  ScalesIcon,
  ShieldCheckIcon,
} from '@phosphor-icons/react';

import { LinkButton } from '@/ui/button/Button';
import { Card } from '@/ui/Card';
import { FieldRow, FieldRows, FieldValue } from '@/features/settings/components/FieldRow';
import { SettingsSection } from '@/features/settings/components/SettingsSection';
import { StatusPill } from '@/features/settings/components/StatusPill';

const TRUST_CENTER_URL = 'https://trust.zooid.app';
const SECURITY_CONTACT = 'security@zooid.app';

type PhosphorIcon = ComponentType<{
  size?: number;
  weight?: 'regular' | 'duotone' | 'fill' | 'bold' | 'thin' | 'light';
  className?: string;
}>;

export function ComplianceContent() {
  return (
    <div className="flex flex-col gap-10">
      <TrustCenterCard />
      <CertificationsSection />
      <DataProtectionSection />
      <SecurityContactSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trust Center
// ---------------------------------------------------------------------------

function TrustCenterCard() {
  return (
    <div className="flex items-center gap-3.5 rounded-lg border border-brand-border bg-brand-tint/40 p-4">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-tile bg-brand-tint text-brand-text">
        <ShieldCheckIcon size={20} weight="duotone" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold text-fg">Trust Center</span>
        <span className="text-xs text-fg-tertiary">Live security status, sub-processors, and audit reports.</span>
      </div>
      <LinkButton
        to={TRUST_CENTER_URL}
        isExternal
        target="_blank"
        variant="primary"
        size={9}
        iconRight={<ArrowSquareOutIcon size={14} />}
      >
        Visit Trust Center
      </LinkButton>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Certifications
// ---------------------------------------------------------------------------

interface Certification {
  icon: PhosphorIcon;
  name: string;
  detail: string;
}

const CERTIFICATIONS: Certification[] = [
  {
    icon: CertificateIcon,
    name: 'ISO 27001',
    detail: 'Information security management',
  },
  {
    icon: ShieldCheckIcon,
    name: 'SOC 2 Type II',
    detail: 'Security, availability, and confidentiality',
  },
];

function CertificationsSection() {
  return (
    <SettingsSection
      title="Certifications"
      description="Independent audits we are working toward. Current status is published on the Trust Center."
    >
      <div className="grid grid-cols-2 gap-2.5">
        {CERTIFICATIONS.map((cert) => {
          const Icon = cert.icon;
          return (
            <Card key={cert.name} radius="lg" padding="sm" className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex size-9 items-center justify-center rounded-sm bg-brand-tint text-brand-text">
                  <Icon size={18} weight="duotone" />
                </div>
                <StatusPill tone="warning">In progress</StatusPill>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-fg">{cert.name}</span>
                <span className="text-xs text-fg-tertiary">{cert.detail}</span>
              </div>
            </Card>
          );
        })}
      </div>
    </SettingsSection>
  );
}

// ---------------------------------------------------------------------------
// Data protection
// ---------------------------------------------------------------------------

interface ProtectionRow {
  icon: PhosphorIcon;
  label: string;
  description: string;
  value: string;
}

const PROTECTION_ROWS: ProtectionRow[] = [
  {
    icon: GlobeStandIcon,
    label: 'Data residency',
    description: 'Where your source code and metadata are stored.',
    value: 'EU regions',
  },
  {
    icon: LockKeyIcon,
    label: 'Encryption in transit',
    description: 'Traffic between your browser and our servers.',
    value: 'TLS · modern cipher suites',
  },
  {
    icon: KeyIcon,
    label: 'Encryption at rest',
    description: 'Database and object storage.',
    value: 'AES-256',
  },
  {
    icon: ScalesIcon,
    label: 'GDPR',
    description: 'Lawful basis, data subject rights, sub-processors, 72-hour breach notice.',
    value: 'Documented',
  },
];

function DataProtectionSection() {
  return (
    <SettingsSection title="Data protection" description="How your code and data are stored, encrypted, and governed.">
      <FieldRows>
        {PROTECTION_ROWS.map((row) => (
          <FieldRow key={row.label} label={row.label} description={row.description}>
            <FieldValue>{row.value}</FieldValue>
          </FieldRow>
        ))}
      </FieldRows>
    </SettingsSection>
  );
}

// ---------------------------------------------------------------------------
// Security contact
// ---------------------------------------------------------------------------

function SecurityContactSection() {
  return (
    <SettingsSection title="Security contact" description="Report a vulnerability or request documentation.">
      <FieldRows>
        <FieldRow label="Security disclosures" description="We respond to reports within one business day.">
          <a href={`mailto:${SECURITY_CONTACT}`} className="text-sm font-medium text-brand-text hover:text-brand-hover">
            {SECURITY_CONTACT}
          </a>
        </FieldRow>
      </FieldRows>
    </SettingsSection>
  );
}
